import { AxiosError } from 'axios';
import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '../../components/Button';
import baseAPI from '../../api';
import RadioButton from '../../components/RadioButton';
import CheckBox from '../../components/CheckBox';
import Analytics from 'analytics';
// @ts-ignore
import googleAnalytics from '@analytics/google-analytics';
import { FormItem, WisoryInput } from 'components/Inputs';
import { getWordpressPageUrl } from 'pages/profile/helper';

export interface PackageOrderFormData {
  customer_name: string;
  company_email: string;
  customer_phone: string;
}

export enum PackageIdType {
  NONE = 'NONE',
  PACKAGE_1 = '1',
  PACKAGE_2 = '2',
  PACKAGE_3 = '3',
}

function PackageOrderForm({ packageId }: { packageId: string }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PackageOrderFormData>();

  const api = baseAPI.api;
  const [loading, setLoading] = useState(false);
  const [successMessage] = useState(false);
  const [isConfirmed, setConfirm] = useState(false);
  const [packageIdValue, setPackageIdValue] = useState<PackageIdType>(PackageIdType.PACKAGE_1);
  const [submittingError, setSubmittingError] = useState<
    AxiosError<{
      errorCode: string;
      errorMessage: string;
    }>[]
  >();

  const getTrackingId = () => {
    const { ENVIRONMENT } = process.env;

    if (ENVIRONMENT === 'prod') {
      return WISORY_SDK_SETTINGS.PRODUCTION_TRACKING_ID ?? 'UA-195815125-1';
    }

    return WISORY_SDK_SETTINGS.LAB_TRACKING_ID ?? 'UA-195815125-1';
  };

  /* initialize analytics */
  const analytics = Analytics({
    app: 'Wisory-App',
    plugins: [
      googleAnalytics({
        trackingId: getTrackingId(),
      }),
    ],
  });

  const onSubmitHandler = async (data: PackageOrderFormData): Promise<void> => {
    if (loading) {
      return;
    }

    setLoading(true);
    setSubmittingError([]);
    try {
      await api.packageOrder({
        customerName: data.customer_name,
        customerEmail: data.company_email,
        customerPhone: data.customer_phone,
        packageId: packageIdValue,
        isTermAccepted: isConfirmed,
      });

      await analytics.track('Submitted', {
        category: 'Form',
        label: 'Advisor time request',
        value: 3000,
      });

      window.location.pathname = getWordpressPageUrl('PACKAGE_CONFIRMATION_URL');
    } catch (error) {
      setSubmittingError([error as AxiosError<{ errorCode: string; errorMessage: string }>]);
    }
    setLoading(false);
  };

  const handleSelectPlatform = useCallback((newPackageIdValue: PackageIdType) => {
    setPackageIdValue(newPackageIdValue);
  }, []);

  const { t } = useTranslation();

  return (
    <div id="order">
      {successMessage ? (
        <div className="text-xl mt-5 text-center text-denim">{t('thank you for ordering')}</div>
      ) : (
        <form onSubmit={handleSubmit(onSubmitHandler)} className="text-white" id="card-checkout-form">
          <div className="font-libreBaskerville mb-2 text-left text-xl italic text-yellow">{t('package order')}</div>

          <div className="font-openSans text-left text-md text-denim">{t('package order title')}</div>

          <div className="mt-5 text-md">
            <RadioButton
              label={t('package_id_1')}
              checked={packageIdValue === PackageIdType.PACKAGE_1}
              onChange={() => handleSelectPlatform(PackageIdType.PACKAGE_1)}
              denim
            />
          </div>
          <div className="mt-2 text-md">
            <RadioButton
              label={t('package_id_2')}
              checked={packageIdValue === PackageIdType.PACKAGE_2}
              onChange={() => handleSelectPlatform(PackageIdType.PACKAGE_2)}
              denim
            />
          </div>
          <div className="mt-2 text-md">
            <RadioButton
              label={t('package_id_3')}
              checked={packageIdValue === PackageIdType.PACKAGE_3}
              onChange={() => handleSelectPlatform(PackageIdType.PACKAGE_3)}
              denim
            />
          </div>

          <div className="font-openSans text-sm mt-5 text-denim required">{t('customer_name')}</div>
          <div className="mt-2">
            <FormItem errorMessage={errors.customer_name?.message}>
              <WisoryInput {...register('customer_name', { required: true })} type="text" required />
            </FormItem>
          </div>

          <div className="font-openSans text-sm mt-5 text-denim required">{t('company_email')}</div>
          <div className="mt-2">
            <FormItem errorMessage={errors.company_email?.message}>
              <WisoryInput
                {...register('company_email', { required: true })}
                type="email"
                pattern="[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                required
              />
            </FormItem>
          </div>

          <div className="font-openSans text-sm mt-5 text-denim required">{t('customer_phone_number')}</div>
          <div className="mt-2">
            <FormItem errorMessage={errors.customer_phone?.message}>
              <WisoryInput {...register('customer_phone', { required: true })} type="text" required />
            </FormItem>
          </div>

          <div className="mt-3 text-md">
            <CheckBox label={t('page rules')} checked={isConfirmed} onChange={() => setConfirm(!isConfirmed)} denim />
          </div>

          {submittingError &&
            submittingError?.map((error) => (
              <div
                className="animate__animated animate__shakeX mt-5 text-sm text-denim"
                key={error.response?.data.errorCode}
              >
                {error.response?.data.errorCode ? t(error.response?.data.errorCode) : ''}
              </div>
            ))}

          <div className="mt-5 md:flex justify-center items-center">
            <Button
              className="text-center max-w-xs mt-5 md:mt-0"
              type="submit"
              loading={loading}
              disabled={!isConfirmed}
            >
              {t('booking package order')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export default PackageOrderForm;
